import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Typography, Stack, InputAdornment, Alert, CircularProgress,
} from '@mui/material';
import { getApiKey, recordManualSale } from '../../databaseManager';
import { deleteProductFromChannel } from '../api';
import { formatStoredDate, isListedOnChannel } from './productUtils';

// Raw values match the `lastSaleChannel` / saleHistory `channel` strings the
// backend's apply_sale writes (iOS SalePlatform), plus off_platform.
export const SALE_PLATFORMS = [
  { value: 'off_platform', label: 'Offline' },
  { value: 'ebay', label: 'eBay' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'square', label: 'Square' },
  { value: 'etsy', label: 'Etsy' },
  { value: 'google_merchant', label: 'Google Merchant' },
];

// <input type="datetime-local"> value for "now", in local time.
const nowLocalInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const parseOptionalNumber = (s) => {
  if (s === '' || s == null) return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
};

/**
 * Manual sale entry (web port of iOS MarkAsSoldView). Writes the same product
 * fields + saleHistory entry as a channel-detected sale; when stock reaches zero
 * the item is delisted from every channel it's on (best effort).
 *
 * Props: open, onClose, customer (id), product, onSold(productPatch)
 */
export default function MarkAsSoldDialog({ open, onClose, customer, product, onSold }) {
  const maxQty = Math.max(1, product?.quantity ?? 1);
  const [platform, setPlatform] = useState('off_platform');
  const [qty, setQty] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [soldAt, setSoldAt] = useState(nowLocalInput());
  const [orderId, setOrderId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPlatform('off_platform');
    setQty(String(maxQty));
    setUnitPrice('');
    setUnitCost('');
    setSoldAt(nowLocalInput());
    setOrderId('');
    setNotes('');
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const quantitySold = Math.min(maxQty, Math.max(1, parseInt(qty, 10) || 1));
      const patch = await recordManualSale(customer, product, {
        platform,
        quantitySold,
        unitPrice: parseOptionalNumber(unitPrice),
        unitCost: parseOptionalNumber(unitCost),
        soldAt: formatStoredDate(new Date(soldAt)),
        orderId: orderId.trim(),
        notes: notes.trim(),
      });
      if (patch.quantity === 0) {
        // Sold out — pull it from every storefront. The product doc stays (now
        // sold out) so it keeps showing on the Sales page.
        const apiKey = await getApiKey(customer);
        const channels = ['shopify', 'ebay', 'square', 'etsy'].filter((c) => isListedOnChannel(product, c));
        await Promise.all(channels.map((c) => deleteProductFromChannel(apiKey, product.productUri, c).catch((err) => {
          console.error(`Failed to delist from ${c} after sale:`, err);
        })));
      }
      onSold(patch);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to record sale');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark as Sold</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} noWrap>
          {product?.specs?.Title || product?.title || 'Untitled'}
        </Typography>
        <Stack spacing={2}>
          <TextField select size="small" label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {SALE_PLATFORMS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>
          <TextField
            size="small" type="number" label="Quantity sold" value={qty}
            onChange={(e) => setQty(e.target.value)}
            inputProps={{ min: 1, max: maxQty }}
            helperText={`${maxQty} in stock`}
          />
          <TextField
            size="small" type="number" label="Sale price (per item)" value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
          <TextField
            size="small" type="number" label="Cost (per item, optional)" value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
          <TextField
            size="small" type="datetime-local" label="Sale date" value={soldAt}
            onChange={(e) => setSoldAt(e.target.value)} InputLabelProps={{ shrink: true }}
          />
          <TextField size="small" label="Order ID (optional)" value={orderId} onChange={(e) => setOrderId(e.target.value)} />
          <TextField size="small" label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={2} maxRows={6} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !soldAt}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
